import React from "react";

/**
 * ErrorBoundary catches render/lifecycle errors from its children so a
 * single component throw does not white-screen the whole dashboard.
 *
 * Introduced 2026-07-02 after prod facilitator dashboards rendered blank
 * with no console errors during a burst of 401s. With this in place, any
 * future render-time crash shows a visible "Something went wrong" card
 * with a Reload button instead of a white page.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleReload = () => {
    // Full reload — clears any stale state that provoked the crash.
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    if (this.state.hasError) {
      const label = this.props.label || "this page";
      const message =
        this.state.error?.message || "Something unexpected happened.";
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-background p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-red-200 p-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto text-red-600 text-2xl">
              !
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              Something went wrong loading {label}
            </h2>
            <p className="text-sm text-slate-600 break-words">{message}</p>
            <p className="text-xs text-slate-400">
              This error was logged. Reloading usually fixes it. If it keeps
              happening, contact your administrator.
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
                data-testid="error-boundary-reload-btn"
              >
                Reload page
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                data-testid="error-boundary-home-btn"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
